import axios, { AxiosError, AxiosResponse } from "axios";
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from "@redux-saga/core/effects";
import { Task } from "@redux-saga/types";

import { RootState } from "data-handler/rootReducer";

const prefix = "schoolconnect/xray";

export const XRAY_TRIGGER_REQUEST = `${prefix}/TRIGGER_REQUEST`;
export const XRAY_TRIGGER_SUCCESS = `${prefix}/TRIGGER_SUCCESS`;
export const XRAY_TRIGGER_FAILURE = `${prefix}/TRIGGER_FAILURE`;

export const XRAY_POLL_UPDATE = `${prefix}/POLL_UPDATE`;
export const XRAY_POLL_STOP = `${prefix}/POLL_STOP`;

export const XRAY_CANCEL_REQUEST = `${prefix}/CANCEL_REQUEST`;
export const XRAY_CANCEL_SUCCESS = `${prefix}/CANCEL_SUCCESS`;
export const XRAY_CANCEL_FAILURE = `${prefix}/CANCEL_FAILURE`;

export const XRAY_RESET = `${prefix}/RESET`;

export type XRayBatchProgress = {
  completed_schools: number;
  total_schools: number;
  completed_stocks: number;
  total_stocks: number;
  total_stocks_all: number | null;
};

export type XRayState = {
  batchId: string | null;
  status:
    | "idle"
    | "triggering"
    | "processing"
    | "completed"
    | "cancelled"
    | "error";
  progress: XRayBatchProgress | null;
  error: string | null;
};

type TriggerRequestAction = {
  type: typeof XRAY_TRIGGER_REQUEST;
  filters: Record<string, string | number>;
};

type TriggerSuccessAction = {
  type: typeof XRAY_TRIGGER_SUCCESS;
  batchId: string;
};

type TriggerFailureAction = {
  type: typeof XRAY_TRIGGER_FAILURE;
  error: string;
};

type PollUpdateAction = {
  type: typeof XRAY_POLL_UPDATE;
  status: string;
  progress: XRayBatchProgress;
};

type PollStopAction = {
  type: typeof XRAY_POLL_STOP;
  status: string;
};

type CancelSuccessAction = {
  type: typeof XRAY_CANCEL_SUCCESS;
};

type CancelFailureAction = {
  type: typeof XRAY_CANCEL_FAILURE;
  error: string;
};

type ResetAction = {
  type: typeof XRAY_RESET;
};

type Action =
  | TriggerRequestAction
  | TriggerSuccessAction
  | TriggerFailureAction
  | PollUpdateAction
  | PollStopAction
  | { type: typeof XRAY_CANCEL_REQUEST }
  | CancelSuccessAction
  | CancelFailureAction
  | ResetAction;

const initialState: XRayState = {
  batchId: null,
  status: "idle",
  progress: null,
  error: null,
};

const reducer = (
  state: XRayState = initialState,
  action: Action
): XRayState => {
  switch (action.type) {
    case XRAY_TRIGGER_REQUEST:
      return { ...initialState, status: "triggering" };
    case XRAY_TRIGGER_SUCCESS:
      return {
        ...state,
        batchId: (action as TriggerSuccessAction).batchId,
        status: "processing",
      };
    case XRAY_TRIGGER_FAILURE:
      return {
        ...state,
        status: "error",
        error: (action as TriggerFailureAction).error,
      };
    case XRAY_POLL_UPDATE: {
      const a = action as PollUpdateAction;
      const resolved =
        a.status === "completed"
          ? "completed"
          : a.status === "cancelled"
          ? "cancelled"
          : "processing";
      return {
        ...state,
        status: resolved,
        progress: a.progress,
      };
    }
    case XRAY_POLL_STOP: {
      const a = action as PollStopAction;
      return {
        ...state,
        status: a.status === "cancelled" ? "cancelled" : state.status,
      };
    }
    case XRAY_CANCEL_SUCCESS:
      return { ...state, status: "cancelled" };
    case XRAY_CANCEL_FAILURE:
      return {
        ...state,
        error: (action as CancelFailureAction).error,
      };
    case XRAY_RESET:
      return initialState;
    default:
      return state;
  }
};

export default reducer;

const baseUrl = () => (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");

function triggerXRayApi(filters: Record<string, string | number>) {
  return axios.post(`${baseUrl()}/store_manager/store-doctor/xray/`, filters);
}

function pollXRayStatusApi(batchId: string) {
  return axios.get(`${baseUrl()}/store_manager/store-doctor/xray/${batchId}/`);
}

function cancelXRayApi(batchId: string) {
  return axios.delete(
    `${baseUrl()}/store_manager/store-doctor/xray/${batchId}/`
  );
}

function buildProgressFromResponse(data: any): XRayBatchProgress {
  if (data.progress) {
    return {
      completed_schools: data.progress.completed_schools ?? 0,
      total_schools: data.progress.total_schools ?? 0,
      completed_stocks: data.progress.completed_stocks ?? 0,
      total_stocks: data.progress.total_stocks ?? 0,
      total_stocks_all: data.progress.total_stocks_all ?? null,
    };
  }

  const chunks = data.chunks ?? [];
  let completedSchools = 0;
  let totalSchools = data.total_schools ?? 0;
  let completedStocks = data.completed_stocks ?? 0;
  let totalStocks = data.total_stocks ?? 0;

  chunks.forEach((c: any) => {
    if (c.status === "completed") {
      completedSchools += (c.school_ids ?? []).length;
    } else if (c.status === "processing" && c.progress) {
      completedSchools += c.progress.current ?? 0;
    }
  });

  return {
    completed_schools: completedSchools,
    total_schools: totalSchools,
    completed_stocks: completedStocks,
    total_stocks: totalStocks,
    total_stocks_all: data.total_stocks_all ?? null,
  };
}

function* pollLoop(batchId: string) {
  while (true) {
    try {
      const resp: AxiosResponse = yield call(pollXRayStatusApi, batchId);
      const data = resp.data;
      const status = data.status as string;
      const progress = buildProgressFromResponse(data);

      const allDone =
        status === "processing" &&
        progress.total_stocks > 0 &&
        progress.completed_stocks >= progress.total_stocks;
      const resolvedStatus = allDone ? "completed" : status;

      yield put({ type: XRAY_POLL_UPDATE, status: resolvedStatus, progress });

      if (resolvedStatus === "completed" || resolvedStatus === "cancelled") {
        yield put({ type: XRAY_POLL_STOP, status: resolvedStatus });
        return;
      }
    } catch {
      yield put({ type: XRAY_POLL_STOP, status: "error" });
      return;
    }

    yield delay(3000);
  }
}

export function* triggerXRaySaga(action: TriggerRequestAction) {
  try {
    const resp: AxiosResponse = yield call(triggerXRayApi, action.filters);

    if (resp.data.cleared) {
      yield put({
        type: XRAY_POLL_UPDATE,
        status: "completed",
        progress: {
          completed_schools: 0,
          total_schools: 0,
          completed_stocks: 0,
          total_stocks: 0,
          total_stocks_all: null,
        },
      });
      yield put({ type: XRAY_POLL_STOP, status: "completed" });
      return;
    }

    const batchId = resp.data.batch_id;
    yield put({ type: XRAY_TRIGGER_SUCCESS, batchId });

    const pollTask: Task = yield fork(pollLoop, batchId);

    const result: Action = yield take([XRAY_CANCEL_REQUEST, XRAY_POLL_STOP]);

    if (result.type === XRAY_CANCEL_REQUEST) {
      yield cancel(pollTask);
      try {
        yield call(cancelXRayApi, batchId);
        yield put({ type: XRAY_CANCEL_SUCCESS });
      } catch (err) {
        const msg =
          (err as AxiosError)?.response?.data?.error ?? "Cancel failed";
        yield put({ type: XRAY_CANCEL_FAILURE, error: msg });
      }
    }
  } catch (err) {
    const axErr = err as AxiosError;
    const msg =
      (axErr?.response?.data as any)?.error ??
      axErr?.message ??
      "Failed to trigger X-ray";
    yield put({ type: XRAY_TRIGGER_FAILURE, error: msg });
  }
}

function checkActiveBatchApi() {
  return axios.get(`${baseUrl()}/store_manager/store-doctor/xray/active/`);
}

function* waitForAuth() {
  for (let i = 0; i < 20; i++) {
    const token: string | null = yield select(
      (s: RootState) => s.auth?.token ?? null
    );
    if (token) return true;
    yield delay(500);
  }
  return false;
}

function* reconnectXRaySaga() {
  try {
    const ready: boolean = yield call(waitForAuth);
    if (!ready) return;
    const resp: AxiosResponse = yield call(checkActiveBatchApi);
    const batchId = resp.data?.batch_id;
    if (!batchId) return;

    yield put({ type: XRAY_TRIGGER_SUCCESS, batchId });

    const pollTask: Task = yield fork(pollLoop, batchId);
    const result: Action = yield take([XRAY_CANCEL_REQUEST, XRAY_POLL_STOP]);

    if (result.type === XRAY_CANCEL_REQUEST) {
      yield cancel(pollTask);
      try {
        yield call(cancelXRayApi, batchId);
        yield put({ type: XRAY_CANCEL_SUCCESS });
      } catch (err) {
        const msg =
          (err as AxiosError)?.response?.data?.error ?? "Cancel failed";
        yield put({ type: XRAY_CANCEL_FAILURE, error: msg });
      }
    }
  } catch {}
}

export function* xraySagaWatcher() {
  yield fork(reconnectXRaySaga);
  yield takeLatest(XRAY_TRIGGER_REQUEST, triggerXRaySaga);
}

export const requestXRayTrigger = (
  filters: Record<string, string | number>
) => ({
  type: XRAY_TRIGGER_REQUEST,
  filters,
});

export const requestXRayCancel = () => ({
  type: XRAY_CANCEL_REQUEST,
});

export const resetXRay = () => ({
  type: XRAY_RESET,
});

export const getXRayState = (state: RootState): XRayState => state.xray;

export const getXRayStatus = (state: RootState) => state.xray.status;

export const getXRayProgress = (state: RootState) => state.xray.progress;

export const getXRayBatchId = (state: RootState) => state.xray.batchId;

export const getXRayError = (state: RootState) => state.xray.error;
